import * as angular from 'angular';
import * as _ from 'lodash';

import { ITagDefinitionService } from '../data/tagDefinition.service';
import { IRequestService } from '../../services';
import { ConnectionPointTestResult, ConnectionPoint, TagDefinition, ConnectionPointQuerySetDto, ConnectionPointDTO } from '../../models';


export interface IConnectionPointService {
    getAll(force?: boolean): angular.IPromise<ConnectionPointDTO[]>;
    getDefault(): angular.IPromise<ConnectionPointDTO>;
    updateOne(connectionPoint: ConnectionPoint, isDatabaseCreationRequested: boolean): angular.IPromise<ConnectionPointDTO[]>;
    update(connectionPointList: any[]): angular.IPromise<any>;
    addOne(connectionPoint: any): angular.IPromise<ConnectionPointDTO>;
    add(connectionPointList: any[]): angular.IPromise<any>;
    testAll(): angular.IPromise<ConnectionPointTestResult[]>;
    test(guid: string): angular.IPromise<ConnectionPointTestResult>;
    testNew(connectionPoint: ConnectionPoint, newPassword: string): angular.IPromise<ConnectionPointTestResult>;
    getByGuid(guid: string, checkUpdate?: boolean): angular.IPromise<ConnectionPointDTO>;
    deleteOne(guid: string): angular.IPromise<number>;
    isUsed(guid: string): angular.IPromise<boolean>;
    duplicate(guid: string): angular.IPromise<ConnectionPointDTO>;
    getWideDatabasesForApplication(guid: string): angular.IPromise<ConnectionPointDTO[]>;
    getList(connectionPointGuids: string[]): angular.IPromise<ConnectionPointDTO[]>;
    getAllQueries(): angular.IPromise<ConnectionPointQuerySetDto[]>;
}

export class ConnectionPointService implements IConnectionPointService {
    static serviceId = 'connectionPointService';

    private _controllerUrl: string;

    static $inject = [
        '$q',
        'tagDefinitionService',
        'requestService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _tagDefinitionService: ITagDefinitionService,
        private _requestService: IRequestService) {
    }

    private controllerUrl() {
        if (!this._controllerUrl) {
            this._controllerUrl = this._requestService.getUrlV1() + 'connectionPoint/';
        }
        return this._controllerUrl;
    }

    getAll(force?: boolean): angular.IPromise<ConnectionPointDTO[]> {
        const url = this.controllerUrl();
        if (force) {
            this._requestService.cleanCache(url);
        }
        return this._requestService.get(url, undefined, true);
    }

    getByGuid(guid: string, checkUpdate = false): angular.IPromise<ConnectionPointDTO> {
        const check = checkUpdate ? '/true/' : '';
        const url = `${this.controllerUrl()}${guid}${check}`;
        return this._requestService.get(url);
    }

    getDefault(): angular.IPromise<ConnectionPointDTO> {
        const url = this.controllerUrl() + 'Default/';
        return this._requestService.get(url, undefined, true);
    }

    addOne(connectionPoint: ConnectionPoint): angular.IPromise<ConnectionPointDTO> {
        const url = this.controllerUrl() + 'add/';
        return this._requestService.post(url, connectionPoint, undefined, true);
    }

    add(connectionPointList: any[]): angular.IPromise<any> {
        const url = this.controllerUrl();
        this._requestService.cleanCache(url);
        return this._requestService.post(url, connectionPointList, undefined, true);
    }

    update(connectionPointList: any[]): angular.IPromise<any> {
        const url = this.controllerUrl();
        this._requestService.cleanCache(url);
        return this._requestService.post(url, connectionPointList, undefined, true);
    }

    deleteOne(guid: string): angular.IPromise<number> {
        const url = this.controllerUrl() + guid + '/';
        this._requestService.cleanCache(url);
        this._requestService.cleanCache(this.controllerUrl());
        return this._requestService.delete(url);
    }

    test(guid: string): angular.IPromise<ConnectionPointTestResult> {
        const url = `${this.controllerUrl()}test/${guid}`;
        this._requestService.cleanCache(url);
        return this._requestService.get(url);
    }

    testNew(connectionPoint: ConnectionPoint, newPassword: string): angular.IPromise<ConnectionPointTestResult> {
        const url = `${this.controllerUrl()}test/`;
        const data = {
            connectionPoint: connectionPoint,
            newPassword: newPassword
        };
        return this._requestService.post(url, data);
    }

    testAll(): angular.IPromise<ConnectionPointTestResult[]> {
        const url = this.controllerUrl() + 'testAll/';
        this._requestService.cleanCache(url);
        return this._requestService.get(url);
    }

    isUsed(guid: string): angular.IPromise<boolean> {
        const promises = [this._tagDefinitionService.getAll(), this.getDefault()];
        return this._$q.all(promises)
            .then((responses: any[]) => {
                const result = _.some(responses[0].map(t => t.tagDefinition), (t: TagDefinition) => {
                    return t.acquisitionPointGuid === guid;
                }) || responses[1].connectionPoint.guid === guid;
                return result;
            });
    }

    duplicate(guid: string): angular.IPromise<ConnectionPointDTO> {
        const url = this.controllerUrl() + 'duplicate/' + guid + '/';
        return this._requestService.post(url);
    }

    getWideDatabasesForApplication(guid: string): angular.IPromise<ConnectionPointDTO[]> {
        const url = this.controllerUrl() + 'application/widedatabases/' + guid + '/';
        return this._requestService.get(url);
    }

    updateOne(connectionPoint: ConnectionPoint, isDatabaseCreationRequested: boolean): angular.IPromise<any> {
        const defered = this._$q.defer();
        const url = this.controllerUrl() + 'updateOne/';
        let frequency: any = {};

        // Remove property not present in ConnectionPoint class to ensure proper mapping on API side
        if (connectionPoint && (connectionPoint as any).frequency) {
            frequency = (connectionPoint as any).frequency;
            delete (connectionPoint as any).frequency;
        }

        const data = {
            connectionPoint: connectionPoint,
            isDatabaseCreationRequested: isDatabaseCreationRequested
        };

        if (frequency) {
            (connectionPoint as any).frequency = frequency;
        }

        this._requestService.cleanCache(this._requestService.getUrlV1() + 'database/isUpToDate/' + connectionPoint.guid + '/');
        this._requestService.post(url, data, undefined, true)
            .then(result => {
                this._requestService.cleanCache(this.controllerUrl());
                this._requestService.cleanCache(this.controllerUrl() + connectionPoint.guid + '/');
                this._requestService.cleanCache(this.controllerUrl() + connectionPoint.guid + '/true/');

                defered.resolve(result);
            }).catch((reason) => {
                defered.reject(reason);
            });
        return defered.promise;
    }

    getList(connectionPointGuids: string[]): angular.IPromise<ConnectionPointDTO[]> {
        const url = this.controllerUrl() + 'getList/';
        const params = {
            guidList: connectionPointGuids
        };
        return this._requestService.get(url, params, true);
    }

    getAllQueries(): angular.IPromise<ConnectionPointQuerySetDto[]> {
        const url = this.controllerUrl() + 'queries/';
        return this._requestService.get(url, undefined, true);
    }
}